import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { Search, ArrowUpRight } from "lucide-react";

const StatusBadge = ({ status }) => {
  const isPaid = status === "paid";
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
        isPaid
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
    >
      {status}
    </span>
  );
};

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/invoices")
      .then((r) => setInvoices(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (filter !== "all" && inv.status !== filter) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        inv.client_name.toLowerCase().includes(q) ||
        inv.invoice_number.toLowerCase().includes(q)
      );
    });
  }, [invoices, query, filter]);

  const stats = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "paid");
    const unpaid = invoices.filter((i) => i.status === "unpaid");
    return {
      total: invoices.reduce((s, i) => s + (i.total || 0), 0),
      paid: paid.reduce((s, i) => s + (i.total || 0), 0),
      outstanding: unpaid.reduce((s, i) => s + (i.total || 0), 0),
      count: invoices.length,
    };
  }, [invoices]);

  return (
    <div className="space-y-10" data-testid="dashboard-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Dashboard
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-zinc-900 mt-2">
            Invoices.
          </h1>
        </div>
        <Link
          to="/new"
          data-testid="dashboard-new-invoice-button"
          className="bg-zinc-900 text-white rounded-none px-6 py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
        >
          + Create new invoice
        </Link>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-y border-zinc-200">
        {[
          { label: "Total Invoices", value: stats.count, testid: "stat-total-count" },
          { label: "Billed", value: formatCurrency(stats.total), testid: "stat-total-billed" },
          { label: "Paid", value: formatCurrency(stats.paid), testid: "stat-total-paid" },
          { label: "Outstanding", value: formatCurrency(stats.outstanding), testid: "stat-outstanding" },
        ].map((s, i) => (
          <div
            key={s.label}
            data-testid={s.testid}
            className={`py-8 px-2 ${i > 0 ? "md:border-l border-zinc-200" : ""}`}
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              {s.label}
            </p>
            <p className="font-display text-3xl font-light text-zinc-900 mt-2">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative max-w-md w-full">
          <Search
            size={16}
            className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <input
            data-testid="search-input"
            placeholder="Search by client or invoice #"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border-0 border-b border-zinc-200 rounded-none pl-6 pr-0 py-3 focus:ring-0 focus:border-zinc-900 bg-transparent outline-none transition-colors placeholder:text-zinc-400"
          />
        </div>
        <div className="flex gap-2">
          {["all", "unpaid", "paid"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
                filter === f
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-transparent text-zinc-600 border-zinc-200 hover:border-zinc-900"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border-t border-zinc-900" data-testid="invoices-table">
        {loading ? (
          <p className="py-10 text-center text-zinc-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="empty-state">
            <p className="text-zinc-400 text-sm">No invoices yet.</p>
            <Link
              to="/new"
              className="inline-block mt-4 text-zinc-900 underline underline-offset-4"
              data-testid="empty-state-create-link"
            >
              Create your first one →
            </Link>
          </div>
        ) : (
          <div className="hidden md:block">
            <div className="grid grid-cols-12 gap-4 py-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
              <div className="col-span-2">Invoice</div>
              <div className="col-span-4">Client</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {filtered.map((inv) => (
              <Link
                to={`/invoices/${inv.id}`}
                key={inv.id}
                data-testid={`invoice-row-${inv.invoice_number}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-zinc-200 hover:bg-zinc-50/70 transition-colors group items-center"
              >
                <div className="col-span-2 font-mono text-sm text-zinc-900">
                  {inv.invoice_number}
                </div>
                <div className="col-span-4 text-zinc-900">{inv.client_name}</div>
                <div className="col-span-2 text-zinc-500 text-sm">
                  {inv.created_at?.slice(0, 10)}
                </div>
                <div className="col-span-2">
                  <StatusBadge status={inv.status} />
                </div>
                <div className="col-span-2 text-right font-medium text-zinc-900 flex items-center justify-end gap-2">
                  {formatCurrency(inv.total, inv.currency)}
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Mobile list */}
        <div className="md:hidden divide-y divide-zinc-200">
          {filtered.map((inv) => (
            <Link
              key={inv.id}
              to={`/invoices/${inv.id}`}
              data-testid={`invoice-card-${inv.invoice_number}`}
              className="block py-5"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="font-mono text-sm">{inv.invoice_number}</span>
                <StatusBadge status={inv.status} />
              </div>
              <p className="text-zinc-900">{inv.client_name}</p>
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className="text-zinc-500">
                  {inv.created_at?.slice(0, 10)}
                </span>
                <span className="font-medium">
                  {formatCurrency(inv.total, inv.currency)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
